<template>
  <div class="editor-page-picker" @keydown.stop="handleKey" @focusout="handleFocusOut">
    <button ref="trigger" type="button" class="editor-page-trigger" :disabled="!contain.projectRecord"
            aria-haspopup="menu" :aria-expanded="String(open)" aria-controls="editor-page-menu"
            :aria-label="'页面：' + currentName" :title="projectName + ' · ' + currentName" @click="toggle">
      <span class="editor-project-label">
        <span class="editor-project-name">{{ currentName }}</span>
        <span id="editor-save-status" class="editor-save-status" :class="'is-' + contain.editorSaveStatus" role="status" aria-live="polite" :title="contain.editorSaveStatusDescription">{{ contain.editorSaveStatusText }}</span>
      </span>
      <i aria-hidden="true" class="el-icon-arrow-down" :class="{ 'is-open': open }"></i>
    </button>
    <button v-if="contain.editorSaveStatus === 'error'" type="button" class="editor-icon-button" aria-label="重试保存" title="重试后台保存（Ctrl + S）" @click="contain.saveEditorOnLifecycle()"><i class="el-icon-refresh" aria-hidden="true"></i></button>
    <div v-if="open" id="editor-page-menu" ref="menu" class="editor-page-menu" role="menu" aria-label="项目页面">
      <div class="editor-page-menu__list">
        <div v-for="(page, index) in pages" :key="page.id" class="editor-page-entry" role="none">
          <button type="button" role="menuitem" class="editor-page-row"
                :class="{ 'is-active': page.id === contain.activePageId }"
                :aria-current="page.id === contain.activePageId ? 'page' : null"
                :data-page-id="page.id" :title="page.name" @click="selectPage(page.id)">
          <span>{{ page.name }}</span>
          <i v-if="page.id === contain.pageProject.homePageId" class="el-icon-house" aria-label="首页" title="首页"></i>
          </button>
          <div class="editor-page-order" role="none">
            <button type="button" role="menuitem" :data-page-id="page.id" data-page-move="up"
                    :disabled="index === 0 || structureBusy" :aria-label="'上移页面：' + page.name" title="上移页面" @click="move(page.id, 'up')"><i class="el-icon-arrow-up" aria-hidden="true"></i></button>
            <button type="button" role="menuitem" :data-page-id="page.id" data-page-move="down"
                    :disabled="index === pages.length - 1 || structureBusy" :aria-label="'下移页面：' + page.name" title="下移页面" @click="move(page.id, 'down')"><i class="el-icon-arrow-down" aria-hidden="true"></i></button>
          </div>
        </div>
      </div>
      <div class="editor-page-menu__commands">
        <button type="button" role="menuitem" @click="add"><i class="el-icon-plus" aria-hidden="true"></i>新增页面</button>
        <button type="button" role="menuitem" :disabled="structureBusy" @click="copy"><i class="el-icon-copy-document" aria-hidden="true"></i>复制页面</button>
        <button type="button" role="menuitem" :disabled="contain.activePageId === contain.pageProject.homePageId" @click="setHome"><i class="el-icon-house" aria-hidden="true"></i>设为首页</button>
        <button type="button" role="menuitem" @click="rename"><i class="el-icon-edit" aria-hidden="true"></i>重命名</button>
        <button v-if="canSaveTemplate" type="button" role="menuitem" @click="saveTemplate"><i class="el-icon-star-off" aria-hidden="true"></i>存为页面模板</button>
        <button type="button" role="menuitem" class="is-danger" :disabled="pages.length <= 1" :title="pages.length <= 1 ? '至少保留一个页面' : ''" @click="remove"><i class="el-icon-delete" aria-hidden="true"></i>删除页面</button>
      </div>
    </div>
    <template-save-dialog v-if="templateVisible" :visible.sync="templateVisible" :snapshot="templateSnapshot" :default-name="templateName" @saved="onTemplateSaved" />
    <page-delete-dialog v-if="deleteVisible" :visible="deleteVisible" :page-id="deletingPageId" @update:visible="closeDelete" />
  </div>
</template>

<script>
import store from '@/store'
import { createPageTemplateSnapshot } from '@/editor/core/component-template'
import TemplateSaveDialog from '@/components/Templates/save-dialog.vue'
import PageDeleteDialog from './page-delete-dialog.vue'
export default {
  components: { TemplateSaveDialog, PageDeleteDialog },
  inject: ['contain'],
  data: () => ({ open: false, templateVisible: false, templateSnapshot: null, templateName: '', deleteVisible: false, deletingPageId: '' }),
  computed: {
    structureBusy () { return !this.contain.projectRecord || this.contain.editorSaveReady === false || !!this.contain.editorVersionSwitching },
    canSaveTemplate () { return store.getters['auth/hasResourcePermission']('visualizationTemplate:save') && store.getters['auth/hasResourcePermission']('visualizationTemplate:query') },
    pages () { return this.contain.pageProject.pages },
    currentName () { return (this.pages.find(page => page.id === this.contain.activePageId) || {}).name || '正在加载' },
    projectName () { return (this.contain.projectRecord || {}).name || '未命名大屏' }
  },
  watch: {
    open (value) { this.$emit('open-change', value) },
    'contain.activePageId' () { this.close(false) },
    'contain.projectRecord' (record) { if (!record) this.close(false) }
  },
  mounted () { document.addEventListener('pointerdown', this.outside) },
  beforeDestroy () {
    document.removeEventListener('pointerdown', this.outside)
  },
  methods: {
    saveTemplate () {
      if (!this.canSaveTemplate || !this.contain.projectRecord) return
      try {
        this.templateSnapshot = createPageTemplateSnapshot(this.contain.config, this.contain.nav)
        this.templateName = this.currentName
        this.close(); this.templateVisible = true
      } catch (error) { this.$message.warning(error.message) }
    },
    onTemplateSaved () {
      this.$message.success('页面模板已保存，可从左侧模板面板应用')
      if (this.contain.$refs.palette) this.contain.$refs.palette.refreshTemplates()
    },
    toggle () { this.open ? this.close(true) : this.show() },
    show () {
      if (!this.contain.projectRecord) return
      this.open = true
      this.$nextTick(() => {
        const current = this.$refs.menu.querySelector('[aria-current="page"]')
        if (current) current.focus()
      })
    },
    close (restoreFocus = true) {
      if (!this.open) return
      this.open = false
      if (restoreFocus) this.$nextTick(() => this.$refs.trigger && this.$refs.trigger.focus())
    },
    outside (event) { if (!this.$el.contains(event.target)) this.close(false) },
    handleFocusOut (event) { if (event.relatedTarget && !this.$el.contains(event.relatedTarget)) this.close(false) },
    handleKey (event) {
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return }
      if (!this.open && ['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); this.show(); return }
      if (!this.open || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
      event.preventDefault()
      const buttons = Array.from(this.$refs.menu.querySelectorAll('button:not(:disabled)'))
      const index = buttons.indexOf(document.activeElement)
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length
      buttons[next].focus()
    },
    async selectPage (id) { if (await this.contain.switchEditorPage(id)) this.close() },
    add () { this.contain.addPage(); this.close() },
    copy () {
      if (this.structureBusy) return
      try {
        if (this.contain.copyPage(this.contain.activePageId)) {
          this.close()
          this.$message.success('页面已复制，可撤销')
        }
      } catch (error) { this.$message.error(error.message || '页面复制失败，请重试') }
    },
    move (id, direction) {
      if (!this.contain.movePage(id, direction)) return
      this.$nextTick(() => {
        if (!this.$refs.menu) return
        const buttons = Array.from(this.$refs.menu.querySelectorAll('button'))
        const preferred = buttons.find(button => button.dataset.pageId === id && button.dataset.pageMove === direction && !button.disabled)
        const current = buttons.find(button => button.dataset.pageId === id && !button.dataset.pageMove)
        if (preferred || current) (preferred || current).focus()
      })
    },
    setHome () { this.contain.setHomePage(this.contain.activePageId); this.close() },
    async rename () {
      const id = this.contain.activePageId
      const projectId = this.contain.id
      const name = this.currentName
      this.close()
      try {
        const { value } = await this.$prompt('页面名称', '重命名页面', {
          inputValue: name, confirmButtonText: '确定', cancelButtonText: '取消',
          inputValidator: value => Boolean(value && value.trim() && value.trim().length <= 60) || '请输入1到60个字符的页面名称',
          closeOnClickModal: false
        })
        if (!this._isDestroyed && this.contain.id === projectId) this.contain.renamePage(id, value)
      } catch (error) { /* 用户取消，不修改页面 */ }
      this.$refs.trigger && this.$refs.trigger.focus()
    },
    remove () {
      if (this.pages.length <= 1 || !this.contain.projectRecord || this.deleteVisible) return
      this.deletingPageId = this.contain.activePageId
      this.close(false)
      this.deleteVisible = true
    },
    closeDelete () {
      this.deleteVisible = false
      this.$nextTick(() => { if (!this._isDestroyed && this.$refs.trigger) this.$refs.trigger.focus() })
    }
  }
}
</script>
